import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, MapPin, Menu, X } from "lucide-react";
import { TID } from "../../lib/testIds";

const NAV_LINKS = [
  { label: "Home", hash: "#top" },
  { label: "Structures", hash: "#gallery" },
  { label: "What We Do", route: "/what-we-do" },
  { label: "About", hash: "#why-us" },
  { label: "Capabilities", hash: "#capabilities" },
  { label: "Contact", hash: "#contact" },
];

const HEADER_ADDRESS = "3200 Wilcrest Drive, Suite 170, Houston, TX 77042";

export const Navbar = ({ onContactClick }) => {
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const onHome = pathname === "/" || pathname === "/structures";
  const prefix = onHome ? "" : "/";
  // Header NEVER hides. Two states only: glass (top of dark hero) / solid white.
  const solid = scrolled || menuOpen;

  useMotionValueEvent(scrollY, "change", (y) => {
    setScrolled(y > 24);
  });

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const handleContact = () => {
    setMenuOpen(false);
    if (document.getElementById("contact")) onContactClick?.();
    else window.location.href = "/#contact";
  };

  const renderLink = (link, className, onClick) => {
    if (link.route) {
      return (
        <Link
          key={link.label}
          to={link.route}
          data-testid="nav-whatwedo-link"
          onClick={onClick}
          className={`${className} ${
            pathname === link.route ? "text-[#0A2540] font-semibold" : ""
          }`}
        >
          {link.label}
        </Link>
      );
    }
    return (
      <a
        key={link.label}
        href={`${prefix}${link.hash}`}
        onClick={onClick}
        className={className}
      >
        {link.label}
      </a>
    );
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: "0%", opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 border-b transition-all duration-300 ${
        solid
          ? "bg-white/95 backdrop-blur-lg border-slate-200/70 shadow-[0_2px_16px_rgba(10,37,64,0.08)]"
          : "bg-white/55 backdrop-blur-md border-white/30"
      }`}
    >
      <div className="w-full px-5 md:px-10 lg:px-14 xl:px-16 h-16 flex items-center justify-between gap-3">
        {/* Original AKR artwork — transparent PNG, no box, no distortion */}
        <a
          href={`${prefix}#top`}
          data-testid={TID.nav.logo}
          className="flex items-center shrink-0"
          onClick={() => setMenuOpen(false)}
        >
          <img
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_akr-renders/artifacts/wu6mc57m_image.png"
            alt="AKR Steel Detailing Services LLC"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
          {NAV_LINKS.map((l) =>
            renderLink(
              l,
              "transition-colors duration-300 text-[#0A2540]/75 hover:text-[#0A2540] whitespace-nowrap"
            )
          )}
        </nav>

        <div className="flex items-center gap-2.5">
          {/* Office address — desktop only */}
          <div
            data-testid="header-address"
            className="hidden 2xl:flex items-center gap-1.5 text-xs font-medium text-[#0A2540]/70 pr-1 whitespace-nowrap"
          >
            <MapPin className="w-3.5 h-3.5 text-[#FCA5A5] shrink-0" strokeWidth={2.25} />
            {HEADER_ADDRESS}
          </div>

          <button
            data-testid={TID.nav.contact}
            onClick={handleContact}
            className="inline-flex items-center gap-1.5 bg-[#FDBA74] hover:bg-[#FCA55C] text-[#0A2540] text-xs md:text-sm font-semibold px-3.5 md:px-4 py-2 rounded-full transition-colors duration-300 uppercase tracking-wide whitespace-nowrap"
          >
            Request a Quote
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>

          {/* Mobile menu toggle */}
          <button
            data-testid="nav-mobile-menu-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full text-[#0A2540] hover:bg-slate-100 transition-colors"
          >
            {menuOpen ? (
              <X className="w-5 h-5" strokeWidth={2.25} />
            ) : (
              <Menu className="w-5 h-5" strokeWidth={2.25} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="md:hidden overflow-hidden bg-white/97 backdrop-blur-lg border-t border-slate-200/70"
          >
            <div className="px-6 py-3 flex flex-col">
              {NAV_LINKS.map((l) =>
                renderLink(
                  l,
                  "py-3.5 text-base font-medium text-[#0A2540]/80 hover:text-[#0A2540] border-b border-slate-100 last:border-b-0 transition-colors",
                  () => setMenuOpen(false)
                )
              )}
              <div
                data-testid="mobile-menu-address"
                className="flex items-start gap-2.5 py-4 mt-1 border-t border-slate-200 text-sm text-[#0A2540]/70"
              >
                <MapPin className="w-4 h-4 text-[#FCA5A5] shrink-0 mt-0.5" strokeWidth={2.25} />
                <span>
                  3200 Wilcrest Drive, Suite 170
                  <br />
                  Houston, Texas 77042, United States
                </span>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
